/**
 * ChunkFormFields — the shared field-rendering surface for both the right-pane
 * `ChunkPreview` editor and the `NewChunkModal`. Both compositions need the
 * same fields with the same UX; only the wrapping chrome (modal vs pane) and
 * the trailing actions differ.
 */
import type { Sector } from '../../types';
import type { ChunkQuadrant, ChunkSource } from '../../hooks/useIntelChunks';
import styles from './ChunkFormFields.module.css';

export interface ChunkFormState {
  sector_id: number;
  week_of: string;
  event_date: string; // ISO date or empty string
  quadrant: ChunkQuadrant;
  bold_lead: string;
  body: string;
  relevance_to_ember: string;
  entities: string;            // CSV
  tags: string;                // CSV
  portfolio_companies: string; // CSV
  sources: ChunkSource[];
}

interface Props {
  state: ChunkFormState;
  sectors: Sector[];
  /** Scheduled meeting Mondays (ISO). Source of truth for the week_of select.
   *  The form's current value is always included so editing an old chunk
   *  doesn't accidentally drop its meeting attribution. */
  meetingDates: string[];
  /** Updater that performs a partial merge — keeps callers free of useState boilerplate. */
  onChange: (patch: Partial<ChunkFormState>) => void;
  /** When true, sector + week_of are read-only (they're metadata, set on create). */
  lockMetadata?: boolean;
}

export const QUADRANT_OPTIONS: { value: ChunkQuadrant; label: string }[] = [
  { value: 'market_trends', label: 'Market Trends' },
  { value: 'news_regulatory', label: 'News & Regulatory' },
  { value: 'transactions', label: 'Transactions' },
  { value: 'capital_markets', label: 'Capital Markets' },
];

export function csvJoin(xs: string[]): string {
  return xs.join(', ');
}

export function csvSplit(s: string): string[] {
  return s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

function formatMeetingLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ChunkFormFields({
  state,
  sectors,
  meetingDates,
  onChange,
  lockMetadata,
}: Props) {
  const set = <K extends keyof ChunkFormState>(k: K, v: ChunkFormState[K]) =>
    onChange({ [k]: v } as Partial<ChunkFormState>);

  // Always include the current value so an off-horizon week_of (e.g., a
  // long-past chunk) stays selectable. De-duplicate, then sort ascending.
  const meetingOptions = (() => {
    const dates = new Set<string>(meetingDates);
    if (state.week_of) dates.add(state.week_of);
    return [...dates].sort();
  })();

  const updateSource = (i: number, next: Partial<ChunkSource>) => {
    set(
      'sources',
      state.sources.map((s, idx) => (idx === i ? { ...s, ...next } : s)),
    );
  };
  const removeSource = (i: number) => {
    set('sources', state.sources.filter((_, idx) => idx !== i));
  };
  const addSource = () => {
    set('sources', [...state.sources, { label: '', url: '' }]);
  };

  return (
    <div className={styles.fields}>
      <div className={styles.row2}>
        <div className={styles.field}>
          <label className={styles.label}>Sector</label>
          <select
            className={styles.select}
            value={state.sector_id}
            onChange={(e) => set('sector_id', Number(e.target.value))}
            disabled={lockMetadata}
          >
            {sectors.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Quadrant</label>
          <select
            className={styles.select}
            value={state.quadrant}
            onChange={(e) => set('quadrant', e.target.value as ChunkQuadrant)}
          >
            {QUADRANT_OPTIONS.map((q) => (
              <option key={q.value} value={q.value}>{q.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className={styles.row2}>
        <div className={styles.field}>
          <label className={styles.label}>Meeting week</label>
          <select
            className={styles.select}
            value={state.week_of}
            onChange={(e) => set('week_of', e.target.value)}
            disabled={lockMetadata}
          >
            {meetingOptions.length === 0 && (
              <option value="">(no scheduled meetings)</option>
            )}
            {meetingOptions.map((iso) => (
              <option key={iso} value={iso}>
                {formatMeetingLabel(iso)}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Event / publication date</label>
          <input
            type="date"
            className={styles.input}
            value={state.event_date}
            onChange={(e) => set('event_date', e.target.value)}
            placeholder="Optional"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Bold lead (≤ 25 words)</label>
        <textarea
          className={styles.textarea}
          rows={2}
          value={state.bold_lead}
          onChange={(e) => set('bold_lead', e.target.value)}
          placeholder="One-sentence declarative takeaway"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Body</label>
        <textarea
          className={styles.textarea}
          rows={5}
          value={state.body}
          onChange={(e) => set('body', e.target.value)}
          placeholder="2–4 sentences of factual context."
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Relevance to Ember</label>
        <textarea
          className={styles.textarea}
          rows={2}
          value={state.relevance_to_ember}
          onChange={(e) => set('relevance_to_ember', e.target.value)}
          placeholder="Why this matters to Ember in this sector"
        />
      </div>

      <div className={styles.row2}>
        <div className={styles.field}>
          <label className={styles.label}>Entities (comma-separated)</label>
          <input
            className={styles.input}
            value={state.entities}
            onChange={(e) => set('entities', e.target.value)}
            placeholder="EPA, DOE, NextEra Energy"
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>Tags (comma-separated)</label>
          <input
            className={styles.input}
            value={state.tags}
            onChange={(e) => set('tags', e.target.value)}
            placeholder="storage, grid, policy"
          />
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Portfolio companies mentioned (comma-separated)</label>
        <input
          className={styles.input}
          value={state.portfolio_companies}
          onChange={(e) => set('portfolio_companies', e.target.value)}
          placeholder="Only if directly referenced or obvious competitor"
        />
      </div>

      <div className={styles.field}>
        <div className={styles.sourceHeader}>
          <label className={styles.label}>Sources</label>
          <button type="button" className={styles.addSourceBtn} onClick={addSource}>
            + Add source
          </button>
        </div>
        {state.sources.length === 0 ? (
          <div className={styles.muted}>No sources yet.</div>
        ) : (
          state.sources.map((s, i) => (
            <div key={i} className={styles.sourceRow}>
              <input
                className={styles.sourceLabel}
                placeholder="Label (e.g., Utility Dive)"
                value={s.label}
                onChange={(e) => updateSource(i, { label: e.target.value })}
              />
              <input
                className={styles.sourceUrl}
                placeholder="https://…"
                value={s.url}
                onChange={(e) => updateSource(i, { url: e.target.value })}
              />
              <button
                type="button"
                className={styles.removeSourceBtn}
                onClick={() => removeSource(i)}
                title="Remove source"
              >
                ✕
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
